import { useEffect, useState } from 'react';
import { API_URL as API } from '../../config/env.js';
import { businessWhatsAppHref } from '../../config/business';
import { adminHeaders, adminAuthErrorMessage } from '../../utils/adminAuth.js';

const EDITION_MODES = [
  {
    id: 'auto',
    label: 'Automatic',
    hint: 'LIVE appears when today’s PDF is uploaded',
  },
  {
    id: 'on',
    label: 'Always on',
    hint: 'Force LIVE even without today’s PDF',
  },
  {
    id: 'off',
    label: 'Hidden',
    hint: 'Hide LIVE chip and edition banner',
  },
];

const WA_TOPIC_LABELS = {
  subscribe: 'How to subscribe',
  payment: 'Paid but paper won’t open',
  login: 'Login / sign-up problem',
  paper: 'Paper not loading',
  refund: 'Refund or receipt',
  other: 'General help',
};

const SITE_SECTIONS = [
  {
    id: 'live',
    step: '01',
    label: 'Live & banner',
    blurb: 'LIVE badge and homepage announcement',
  },
  {
    id: 'markets',
    step: '02',
    label: 'Market rates',
    blurb: 'Petrol / diesel for the app home strip (gold auto-updates)',
  },
  {
    id: 'whatsapp',
    step: '03',
    label: 'Reader WhatsApp',
    blurb: 'Help buttons readers tap on Contact',
  },
  {
    id: 'team',
    step: '04',
    label: 'Team shortcuts',
    blurb: 'Staff-only quick WhatsApp replies',
  },
];

export default function AdminSiteSettings({ token, onLogout, showToast }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [siteTab, setSiteTab] = useState('live');
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/site-settings`, { headers: adminHeaders(token) });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) return showToast(json.error || 'Could not load settings', 'error');
      setSettings(json);
      setDirty(false);
    } catch {
      showToast('Network error loading settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/site-settings`, {
        method: 'PUT',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401) return onLogout(adminAuthErrorMessage(res, json));
      if (!res.ok) return showToast(json.error || 'Save failed', 'error');
      setSettings(json.settings);
      setDirty(false);
      showToast('Site settings saved');
    } catch {
      showToast('Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const patch = (key, value) => {
    setDirty(true);
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const patchAnn = (key, value) => {
    setDirty(true);
    setSettings((s) => ({ ...s, announcement: { ...s.announcement, [key]: value } }));
  };

  const patchMarket = (key, value) => {
    setDirty(true);
    setSettings((s) => ({
      ...s,
      marketRates: {
        tg: { city: 'Hyderabad', petrol: null, diesel: null },
        ap: { city: 'Vijayawada', petrol: null, diesel: null },
        updatedAt: null,
        ...(s.marketRates || {}),
        [key]: value,
      },
    }));
  };

  const patchFuelRegion = (region, key, value) => {
    setDirty(true);
    setSettings((s) => {
      const mr = s.marketRates || {};
      const prev = mr[region] || {};
      return {
        ...s,
        marketRates: {
          tg: { city: 'Hyderabad', petrol: null, diesel: null },
          ap: { city: 'Vijayawada', petrol: null, diesel: null },
          ...mr,
          [region]: { ...prev, [key]: value },
        },
      };
    });
  };

  const patchWa = (index, key, value) => {
    setDirty(true);
    setSettings((s) => {
      const waTemplates = [...(s.waTemplates || [])];
      waTemplates[index] = { ...waTemplates[index], [key]: value };
      return { ...s, waTemplates };
    });
  };

  const patchShortcut = (index, key, value) => {
    setDirty(true);
    setSettings((s) => {
      const supportShortcuts = [...(s.supportShortcuts || [])];
      supportShortcuts[index] = { ...supportShortcuts[index], [key]: value };
      return { ...s, supportShortcuts };
    });
  };

  const addShortcut = () => {
    setDirty(true);
    setSettings((s) => ({
      ...s,
      supportShortcuts: [
        ...(s.supportShortcuts || []),
        { id: `shortcut_${Date.now()}`, label: 'New shortcut', message: 'Hello,' },
      ],
    }));
  };

  const removeShortcut = (index) => {
    setDirty(true);
    setSettings((s) => ({
      ...s,
      supportShortcuts: (s.supportShortcuts || []).filter((_, i) => i !== index),
    }));
  };

  if (loading || !settings) {
    return (
      <div className="adm-panel">
        <div className="adm-empty">Loading site settings…</div>
      </div>
    );
  }

  const ann = settings.announcement || {};
  const annPreviewStyle =
    ann.style === 'warn'
      ? { background: '#fff8e6', borderColor: '#e8b923', color: '#7a5a00' }
      : { background: '#e8f5ec', borderColor: '#0d5c2e', color: '#0a3d1f' };

  const activeSection = SITE_SECTIONS.find((s) => s.id === siteTab) || SITE_SECTIONS[0];

  return (
    <div className="adm-panel adm-settings">
      <div className="adm-panel__note">
        Controls what readers see on the public site. Upload PDFs from Daily Edition or Monthly Magazine.
      </div>

      <nav className="adm-settings-nav" aria-label="Site settings sections">
        {SITE_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`adm-settings-nav__item ${siteTab === s.id ? 'active' : ''}`}
            onClick={() => setSiteTab(s.id)}
          >
            <span className="adm-settings-nav__step">{s.step}</span>
            <span className="adm-settings-nav__copy">
              <strong>{s.label}</strong>
              <em>{s.blurb}</em>
            </span>
          </button>
        ))}
      </nav>

      <section className="adm-panel__body">
        <header className="adm-panel__section-head">
          <div>
            <p className="adm-panel__eyebrow">Section {activeSection.step}</p>
            <h3>{activeSection.label}</h3>
            <p>{activeSection.blurb}</p>
          </div>
        </header>

        {siteTab === 'live' && (
          <div className="adm-settings-stack">
            <div className="adm-settings-block">
              <div className="adm-settings-block__head">
                <h4>LIVE edition badge</h4>
                <span className="adm-chip">Header + banner</span>
              </div>
              <p className="adm-settings-block__hint">
                Choose when the green LIVE chip and “today’s edition” banner appear.
              </p>
              <div className="adm-choice-grid">
                {EDITION_MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`adm-choice ${settings.editionLiveMode === m.id ? 'active' : ''}`}
                    onClick={() => patch('editionLiveMode', m.id)}
                  >
                    <span className="adm-choice__radio" aria-hidden />
                    <span className="adm-choice__copy">
                      <strong>{m.label}</strong>
                      <span>{m.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="adm-settings-block">
              <div className="adm-settings-block__head">
                <h4>Homepage announcement</h4>
                <label className="adm-switch">
                  <input
                    type="checkbox"
                    checked={!!ann.active}
                    onChange={(e) => patchAnn('active', e.target.checked)}
                  />
                  <span className="adm-switch__track" />
                  <span className="adm-switch__label">{ann.active ? 'On' : 'Off'}</span>
                </label>
              </div>
              <p className="adm-settings-block__hint">
                Short strip at the top of the homepage for offers, maintenance, or news.
              </p>

              {ann.active && (ann.text || ann.textTe) && (
                <div className="adm-ann-preview" style={annPreviewStyle}>
                  <span className="adm-ann-preview-label">Preview</span>
                  {ann.text || ann.textTe}
                </div>
              )}

              <div className="adm-form">
                <div className="adm-row">
                  <div className="adm-field">
                    <label>Message (English)</label>
                    <input
                      type="text"
                      value={ann.text || ''}
                      onChange={(e) => patchAnn('text', e.target.value)}
                      placeholder="e.g. New monthly magazine is out!"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Message (Telugu)</label>
                    <input
                      type="text"
                      value={ann.textTe || ''}
                      onChange={(e) => patchAnn('textTe', e.target.value)}
                      placeholder="తెలుగు సందేశం"
                    />
                  </div>
                </div>
                <div className="adm-row">
                  <div className="adm-field">
                    <label>Optional link</label>
                    <input
                      type="url"
                      value={ann.href || ''}
                      onChange={(e) => patchAnn('href', e.target.value)}
                      placeholder="https://repatikosam.com/subscribe"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Bar colour</label>
                    <select
                      value={ann.style || 'info'}
                      onChange={(e) => patchAnn('style', e.target.value)}
                    >
                      <option value="info">Green (info)</option>
                      <option value="warn">Gold (important)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {siteTab === 'markets' && (
          <div className="adm-settings-stack">
            <div className="adm-settings-block">
              <div className="adm-settings-block__head">
                <h4>Petrol & diesel (AP + Telangana)</h4>
                <span className="adm-chip">Native Markets strip</span>
              </div>
              <p className="adm-settings-block__hint">
                Gold and silver update automatically. Enter today’s petrol and diesel for Hyderabad
                (Telangana) and Vijayawada (AP). Leave blank to use built-in defaults.
              </p>
              <div className="adm-form">
                <div className="adm-field">
                  <label>Last updated</label>
                  <input type="text" value={settings.marketRates?.updatedAt || '—'} readOnly />
                </div>

                <h5 style={{ margin: '12px 0 6px', fontSize: 13 }}>Telangana · Hyderabad</h5>
                <div className="adm-row">
                  <div className="adm-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={settings.marketRates?.tg?.city || ''}
                      onChange={(e) => patchFuelRegion('tg', 'city', e.target.value)}
                      placeholder="Hyderabad"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Petrol (₹ / L)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.marketRates?.tg?.petrol ?? ''}
                      onChange={(e) =>
                        patchFuelRegion('tg', 'petrol', e.target.value === '' ? null : e.target.value)
                      }
                      placeholder="116.15"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Diesel (₹ / L)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.marketRates?.tg?.diesel ?? ''}
                      onChange={(e) =>
                        patchFuelRegion('tg', 'diesel', e.target.value === '' ? null : e.target.value)
                      }
                      placeholder="103.98"
                    />
                  </div>
                </div>

                <h5 style={{ margin: '16px 0 6px', fontSize: 13 }}>Andhra Pradesh · Vijayawada</h5>
                <div className="adm-row">
                  <div className="adm-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={settings.marketRates?.ap?.city || ''}
                      onChange={(e) => patchFuelRegion('ap', 'city', e.target.value)}
                      placeholder="Vijayawada"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Petrol (₹ / L)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.marketRates?.ap?.petrol ?? ''}
                      onChange={(e) =>
                        patchFuelRegion('ap', 'petrol', e.target.value === '' ? null : e.target.value)
                      }
                      placeholder="117.72"
                    />
                  </div>
                  <div className="adm-field">
                    <label>Diesel (₹ / L)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.marketRates?.ap?.diesel ?? ''}
                      onChange={(e) =>
                        patchFuelRegion('ap', 'diesel', e.target.value === '' ? null : e.target.value)
                      }
                      placeholder="105.40"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {siteTab === 'whatsapp' && (
          <div className="adm-settings-block">
            <div className="adm-settings-block__head">
              <h4>Help topics for readers</h4>
              <span className="adm-chip">Contact + mobile help</span>
            </div>
            <p className="adm-settings-block__hint">
              Each topic opens WhatsApp with a ready message. Shown on the Contact page and mobile help sheet.
            </p>

            <div className="adm-wa-list">
              {(settings.waTemplates || []).map((tpl, i) => (
                <details key={tpl.id || i} className="adm-wa-item" open={i === 0}>
                  <summary>
                    <span className="adm-wa-item__topic">{WA_TOPIC_LABELS[tpl.id] || tpl.id}</span>
                    <span className="adm-wa-item__label">{tpl.labelEn || tpl.labelTe || 'No label yet'}</span>
                  </summary>
                  <div className="adm-wa-item__body">
                    <div className="adm-row">
                      <div className="adm-field">
                        <label>Button text (English)</label>
                        <input
                          value={tpl.labelEn || ''}
                          onChange={(e) => patchWa(i, 'labelEn', e.target.value)}
                          placeholder="How do I subscribe?"
                        />
                      </div>
                      <div className="adm-field">
                        <label>Button text (Telugu)</label>
                        <input
                          value={tpl.labelTe || ''}
                          onChange={(e) => patchWa(i, 'labelTe', e.target.value)}
                          placeholder="సభ్యత్వం ఎలా?"
                        />
                      </div>
                    </div>
                    <div className="adm-field">
                      <label>WhatsApp message (English)</label>
                      <textarea
                        rows={2}
                        value={tpl.messageEn || ''}
                        onChange={(e) => patchWa(i, 'messageEn', e.target.value)}
                        placeholder="Hello, I need help with..."
                      />
                    </div>
                    <div className="adm-field">
                      <label>WhatsApp message (Telugu)</label>
                      <textarea
                        rows={2}
                        value={tpl.messageTe || ''}
                        onChange={(e) => patchWa(i, 'messageTe', e.target.value)}
                        placeholder="నమస్కారం..."
                      />
                    </div>
                    <a
                      href={businessWhatsAppHref(tpl.messageEn || tpl.messageTe || '')}
                      target="_blank"
                      rel="noreferrer"
                      className="adm-btn adm-btn-ghost adm-btn-sm"
                    >
                      Test in WhatsApp ↗
                    </a>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {siteTab === 'team' && (
          <div className="adm-settings-block">
            <div className="adm-settings-block__head">
              <h4>Staff quick replies</h4>
              <button type="button" className="adm-btn adm-btn-ghost adm-btn-sm" onClick={addShortcut}>
                + Add shortcut
              </button>
            </div>
            <p className="adm-settings-block__hint">
              For your team only — not shown to readers. Opens WhatsApp with a pre-filled reply.
            </p>

            {(settings.supportShortcuts || []).length === 0 ? (
              <div className="adm-empty">No shortcuts yet. Add one for common support replies.</div>
            ) : (
              <div className="adm-team-list">
                {(settings.supportShortcuts || []).map((sc, i) => (
                  <div key={sc.id || i} className="adm-team-row">
                    <div className="adm-field">
                      <label>Name</label>
                      <input
                        value={sc.label || ''}
                        onChange={(e) => patchShortcut(i, 'label', e.target.value)}
                        placeholder="e.g. Payment issue"
                      />
                    </div>
                    <div className="adm-field adm-team-row__msg">
                      <label>Pre-filled message</label>
                      <input
                        value={sc.message || ''}
                        onChange={(e) => patchShortcut(i, 'message', e.target.value)}
                        placeholder="Hello, I need help with..."
                      />
                    </div>
                    <div className="adm-team-row__actions">
                      <a
                        href={businessWhatsAppHref(sc.message || '')}
                        target="_blank"
                        rel="noreferrer"
                        className="adm-btn adm-btn-primary adm-btn-sm"
                      >
                        Open
                      </a>
                      <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => removeShortcut(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <div className={`adm-panel__save ${dirty ? 'is-dirty' : ''}`}>
        {dirty ? <span>Unsaved changes</span> : <span>All changes saved</span>}
        <button type="button" className="adm-btn adm-btn-primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
